import { motion, useScroll, useTransform } from "framer-motion";
import { useEffect, useRef, useLayoutEffect, useState } from "react";
import logo from "../assets/fme_logo.svg";
import EditionName from "./EditionName";
import { formatEventDate } from "../utils/formatEventDate"; // utilitaire pour formater la date

interface EventData {
  date: string;
  start: string;
  location: string;
}

export default function Hero() {
  const heroRef = useRef<HTMLDivElement>(null);
  const logoRef = useRef<SVGSVGElement | null>(null);
  const { scrollY } = useScroll();
  const [heroHeight, setHeroHeight] = useState(0);
  const [event, setEvent] = useState<null | (EventData & { formattedDate: string })>(null);

  const measureHero = () => {
    if (heroRef.current) setHeroHeight(heroRef.current.offsetHeight);
  };

  // Mesure de la hauteur de la section pour normaliser l'effet
  useLayoutEffect(() => {
    measureHero();
    window.addEventListener("resize", measureHero);
    return () => window.removeEventListener("resize", measureHero);
  }, []);

  const offset = useTransform(scrollY, [0, heroHeight], [0, 100]);
  const darken = useTransform(scrollY, [0, heroHeight * 0.9], [0, 1]);

  // --- Application des offsets aux formes internes du SVG ---
  useEffect(() => {
    const applyOffset = (id: string, offX: number, offY: number) => {
      const el = logoRef.current?.querySelector(`#${id}`);
      if (el) (el as SVGGElement).setAttribute("transform", `translate(${offX}, ${offY})`);
    };

    const unsub = offset.on("change", (offt) => {
      applyOffset("topArrow", 0, -offt);
      applyOffset("rightArrow", offt, 0);
      applyOffset("bottomArrow", 0, offt);
      applyOffset("leftArrow", -offt, 0);

      // Top Right
      applyOffset("redCircles", offt * 0.6, -offt * 0.4);
      applyOffset("darkBlueCircleLarge", offt*2, -offt * 1.5);
      applyOffset("redTriangleFilledTopRight", offt * 0.8, -offt * 0.25);
      applyOffset("redCircleDashed", offt * 0.8, -offt * 1.4);
      applyOffset("yellowLinesDashedTopRight", offt * 0.7, -offt * 0.7);
      applyOffset("blueTriangleDashed", offt * 1.1, -offt * 1.1);
      applyOffset("orangeTriangleFilledBig", offt * 2.7, -offt * 2.7);
      applyOffset("blueTriangleFilledTopRight", offt * 0.3, -offt * 2.7);
      applyOffset("orangeTriangleTopRight", offt * 0.2, -offt * 1.8);
      
      // Bottom Right
      applyOffset("orangeHexagon", offt, offt * 1.5);
      applyOffset("blueDiskHashedBottomRight", offt * 2.1, offt * 2.5);
      applyOffset("redDiskStain", offt*2.2, offt * 2.6);
      applyOffset("darkBlueCircleBottomRight", offt*1.2, offt * 1.6);
      applyOffset("yellowCircleBottomRight", offt*0.6, offt * 0.8);
      applyOffset("redTriangleFilledBottomRight", offt * 0.6, offt * 0.6);
      applyOffset("yellowTriangleFilledSmall", offt * 1.9, offt * 1.6);
      applyOffset("yellowTriangleFilledBig", offt, offt * 1.9);
      
      // Bottom Left
      applyOffset("redDottedCircleBig", -offt * 0.4, offt * 2);
      applyOffset("orangeDiskStain", -offt * 0.9, offt * 0.2);
      applyOffset("stainOrangeDisk", -offt * 0.9, offt * 0.2);
      applyOffset("yellowWave", -offt * 1.8, offt * 1.2);
      applyOffset("blueWave", -offt * 0.9, offt * 1.4);
      applyOffset("blueTriangleStain", -offt * 0.2, offt * 1.7);
      applyOffset("stainBlueTriangleSmall", -offt * 0.2, offt * 1.7);
      applyOffset("yellowLinesDashedBottomLeft", -offt * 0.7, offt * 0.7);
      applyOffset("darkBlueTriangleBottomLeft", -offt * 0.3, offt * 0.3);
      applyOffset("orangeCircleBottomLeft", -offt * 0.5, offt * 0.5);
      
      // Top Left
      applyOffset("redDiskWavesTopLeft", -offt * 3.1, -offt * 1.3);
      applyOffset("orangeCircleTopLeft", -offt * 1.5, -offt * 0.6);
      applyOffset("yellowDottedCircleBig", -offt * 1.1, -offt * 0.7);
      applyOffset("blueDiskHashedTopLeft", -offt * 1.5, -offt * 2.3);
      applyOffset("blueTriangleFilledTopLeft", -offt * 0.9, -offt * 0.4);
      applyOffset("redDiskSmallTop", -offt * 0.3, -offt * 0.2);
    });

    return () => unsub();
  }, [offset]);

  // Fetch event depuis JSON
  useEffect(() => {
    fetch("/data/event.json")
      .then((res) => res.json())
      .then((data: EventData) => {
        setEvent({ ...data, formattedDate: formatEventDate(data.date) });
      })
      .catch(console.error);
  }, []);

  return (
    <section
      ref={heroRef}
      className="relative h-screen flex flex-col items-center justify-center bg-cover bg-center"
      style={{ backgroundImage: "url(/images/hero.jpg)" }}
    >
      {/* Darken */}
      <motion.div
        className="absolute inset-0 bg-black pointer-events-none"
        style={{ opacity: darken }}
      />

      {/* Logo FME animé */}
      <motion.div className="relative w-72 md:w-[400px] h-auto mb-8 drop-shadow-xl overflow-visible">
        <object
          aria-label="Logo du FME"
          ref={(node) => {
            if (node) {
              node.addEventListener("load", () => {
                const svgDoc = (node as HTMLObjectElement).contentDocument;
                if (svgDoc) logoRef.current = svgDoc.querySelector("svg");
              });
            }
          }}
          data={logo}
          type="image/svg+xml"
          className="w-full h-auto pointer-events-none"
        />
      </motion.div>

      {/* Nom de l'édition */}
      <EditionName />

      {/* Date du festival */}
      {event && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 1, ease: "easeOut" }}
          className="absolute bottom-5"
        >
          <div
            className="bg-bleuFonce px-12 py-4 text-center"
            style={{ clipPath: "polygon(0% 0%, 100% 0%, 96% 100%, 4% 100%)" }}
          >
            <span className="text-white text-4xl md:text-6xl font-titan">
              {event.formattedDate}
            </span>
          </div>
          <div className="text-center pt-5">
            <span className="text-white font-bebas text-3xl md:text-4xl">
              À partir de {event.start} | {event.location}
            </span>
          </div>
        </motion.div>
      )}
    </section>
  );
}